/**
 * Rates Service
 * Handles fetching live rates from the GoForexFx rates API
 */

const RATES_API_URL = 'https://goforexfx.com/rates.php';

/**
 * Fetch live rates from the API
 * @returns {Promise<Array>} Array of rate objects
 */
export const fetchRates = async () => {
  try {
    const response = await fetch(RATES_API_URL, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching rates:', error);
    throw error;
  }
};

/**
 * Get rate for a specific currency/symbol
 * @param {Array} rates - Array of all rates
 * @param {string} symbol - Currency symbol (e.g., 'EUR/USD', 'GOLD', 'XAU/USD', 'EURUSD')
 * @returns {Object|null} Rate object or null if not found
 */
export const getRateBySymbol = (rates, symbol) => {
  if (!rates || !Array.isArray(rates) || !symbol) return null;
  
  const symbolUpper = symbol.toUpperCase();
  
  // Try exact match first
  let rate = rates.find(r => r.currency === symbol);
  if (rate) return rate;
  
  // Try case-insensitive match
  rate = rates.find(r => r.currency?.toUpperCase() === symbolUpper);
  if (rate) return rate;
  
  // Try matching without slash (EUR/USD -> EURUSD)
  const symbolNoSlash = symbolUpper.replace(/\//g, '');
  rate = rates.find(r => {
    const currencyNoSlash = r.currency?.toUpperCase().replace(/\//g, '');
    return currencyNoSlash === symbolNoSlash;
  });
  if (rate) return rate;
  
  // For gold, try 'GOLD' or 'XAU/USD' or 'XAUUSD'
  if (symbolUpper.includes('GOLD') || symbolUpper.includes('XAU')) {
    rate = rates.find(r => {
      const currency = r.currency?.toUpperCase();
      return currency === 'GOLD' || currency === 'XAU/USD' || currency === 'XAUUSD';
    });
    if (rate) return rate;
  }
  
  // Try reverse match (EURUSD -> EUR/USD)
  if (symbolUpper.length === 6 && !symbolUpper.includes('/')) {
    const formatted = `${symbolUpper.substring(0, 3)}/${symbolUpper.substring(3)}`;
    rate = rates.find(r => r.currency?.toUpperCase() === formatted);
    if (rate) return rate;
  }
  
  return null;
};

/**
 * Format number with all decimal places (preserves original precision from API)
 * @param {number|string} value - Number to format
 * @returns {string} Formatted number string with all decimals
 */
export const formatWithAllDecimals = (value) => {
  if (value === null || value === undefined) return '0';
  
  // If it's already a string, return as is (preserves original format from API)
  if (typeof value === 'string') {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? '0' : value;
  }
  
  // If it's a number, convert to string
  const numValue = typeof value === 'number' ? value : parseFloat(value);
  if (isNaN(numValue)) return '0';
  
  // Convert to string to preserve precision
  return numValue.toString();
};
